/**
 * Blog template: filtering and pagination without a reload.
 *
 * The server already renders a working page — category links, numbered pages,
 * a 404 past the end. This script only takes the reload out of them, and the
 * mode it uses is the page's own setting (Blog page → Pagination):
 *
 *   numbers  the numbers stay, clicking one swaps the list
 *   more     the numbers become a button that appends the next page
 *   scroll   the same button, pressed for you when it comes into view
 *
 * Every fetch goes to /goodface/v1/posts, which renders through the same PHP as
 * the page itself — so an appended card is identical to a reloaded one. If a
 * request fails, the click is handed back to the browser and the link works the
 * ordinary way.
 *
 * Loaded only on pages using page-blog.php (inc/templates.php).
 */

import { reducedMotion, STAGGER } from '../../js/utils/motion';

/** How far below the fold the button is pressed for you in scroll mode. */
const REACH = 200;

type Payload = {
  page: number;
  maxPages: number;
  category: string;
  featured: string;
  items: string;
  pagination: string;
  empty: string;
};

const setup = (root: HTMLElement): void => {
  const filters = document.querySelector<HTMLElement>('[data-blog-filters]');
  const featured = root.querySelector<HTMLElement>('[data-blog-featured]');
  const grid = root.querySelector<HTMLElement>('[data-blog-grid]');
  const empty = root.querySelector<HTMLElement>('[data-blog-empty]');
  const pagination = root.querySelector<HTMLElement>('[data-blog-pagination]');
  const status = root.querySelector<HTMLElement>('[data-blog-status]');
  const endpoint = root.dataset.rest || '';
  const pageId = root.dataset.pageId || '';
  const pageUrl = root.dataset.pageUrl || '';
  const mode = root.dataset.mode || 'numbers';

  if (!grid || !featured || !pagination || !endpoint || !pageId) {
    return;
  }

  let category = root.dataset.category || '';
  let page = Number(root.dataset.page || 1);
  let max = Number(root.dataset.max || 1);
  let busy = false;

  /** The address this state would have had on a reload. */
  const urlFor = (cat: string, target: number): string => {
    const base = target > 1 ? `${pageUrl.replace(/\/$/, '')}/page/${target}/` : pageUrl;

    return cat ? `${base}?category=${encodeURIComponent(cat)}` : base;
  };

  /** Cards arrive one after another rather than in a block, as elsewhere. */
  const reveal = (cards: HTMLElement[]): void => {
    if (reducedMotion()) {
      cards.forEach((card) => card.classList.add('is-revealed'));
      return;
    }

    cards.forEach((card, index) => {
      window.setTimeout(() => card.classList.add('is-revealed'), index * STAGGER.row);
    });
  };

  const announce = (text: string): void => {
    if (status && text) {
      status.textContent = text;
    }
  };

  /** Labels come from the server so they are translated with everything else. */
  const label = (name: string, ...values: string[]): string => {
    const template = root.dataset[name] || '';

    return values.reduce((text, value, index) => text.replace(`%${index + 1}`, value), template);
  };

  /**
   * The numbers are what the server sends. In the other two modes they are
   * replaced by a button — a real button, because "scroll until more appears"
   * is not something a keyboard can do.
   */
  const paint = (): void => {
    if ('numbers' === mode) {
      return;
    }

    pagination.innerHTML = '';

    if (page >= max) {
      return;
    }

    const button = document.createElement('button');

    button.type = 'button';
    button.className = 'button button--secondary blog__more';
    button.dataset.blogMore = '';
    button.textContent = label('labelMore');

    pagination.appendChild(button);
  };

  const load = async (cat: string, target: number, append: boolean): Promise<boolean> => {
    if (busy) {
      return false;
    }

    busy = true;
    root.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');

    try {
      const url = `${endpoint}?page_id=${encodeURIComponent(pageId)}&page=${target}&category=${encodeURIComponent(cat)}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });

      if (!response.ok) {
        throw new Error(String(response.status));
      }

      const data = (await response.json()) as Payload;
      const holder = document.createElement('div');

      holder.innerHTML = data.items;
      const cards = Array.from(holder.children) as HTMLElement[];

      if (append) {
        cards.forEach((card) => grid.appendChild(card));
      } else {
        grid.replaceChildren(...cards);
        featured.innerHTML = data.featured;

        if (empty) {
          empty.innerHTML = data.empty;
        }
      }

      reveal(cards);

      category = data.category;
      page = data.page;
      max = data.maxPages;

      root.dataset.category = category;
      root.dataset.page = String(page);
      root.dataset.max = String(max);

      // The server-rendered numbers go in first; paint() turns them into a
      // button afterwards when the mode asks for one.
      pagination.innerHTML = data.pagination;
      paint();

      announce(
        append
          ? label('labelAppended', String(cards.length))
          : label('labelLoaded', String(page), String(Math.max(1, max)))
      );

      return true;
    } catch {
      // A failed fetch is not a dead end: the caller follows the real link.
      return false;
    } finally {
      busy = false;
      root.classList.remove('is-loading');
      root.removeAttribute('aria-busy');
    }
  };

  /** Marks the pill for the view now on screen. */
  const markFilter = (cat: string): void => {
    filters?.querySelectorAll<HTMLElement>('[data-category]').forEach((link) => {
      const on = (link.dataset.category || '') === cat;

      link.classList.toggle('is-active', on);

      if (on) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const goTo = (): void => {
    root.focus({ preventScroll: true });
    root.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  };

  const appendNext = (button: HTMLButtonElement): void => {
    button.disabled = true;

    void load(category, page + 1, true).then((ok) => {
      if (!ok) {
        // Nothing was appended, so the numbers are the way forward again.
        button.disabled = false;
        return;
      }

      autoLoad();
    });
  };

  /**
   * In scroll mode the freshly painted button may already be on screen — the
   * appended cards are rarely tall enough to push it below the fold — and no
   * new intersection would be reported for it. So the reach is measured again
   * after every append.
   */
  function autoLoad(): void {
    if ('scroll' !== mode || busy || !pagination) {
      return;
    }

    const button = pagination.querySelector<HTMLButtonElement>('[data-blog-more]');

    if (button && pagination.getBoundingClientRect().top < window.innerHeight + REACH) {
      appendNext(button);
    }
  }

  // Filters ------------------------------------------------------------------

  filters?.addEventListener('click', (event) => {
    const link = (event.target as HTMLElement).closest<HTMLAnchorElement>('[data-category]');

    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) {
      return;
    }

    const next = link.dataset.category || '';

    event.preventDefault();

    if (next === category && 1 === page) {
      return;
    }

    void load(next, 1, false).then((ok) => {
      if (!ok) {
        window.location.href = link.href;
        return;
      }

      markFilter(next);
      window.history.pushState({ category: next, page: 1 }, '', urlFor(next, 1));
      goTo();
      autoLoad();
    });
  });

  // Pagination ---------------------------------------------------------------

  pagination.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const more = target.closest<HTMLButtonElement>('[data-blog-more]');

    if (more) {
      event.preventDefault();
      appendNext(more);

      return;
    }

    const link = target.closest<HTMLAnchorElement>('a');

    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) {
      return;
    }

    event.preventDefault();

    const match = link.getAttribute('href')?.match(/\/page\/(\d+)/);
    const next = match ? Number(match[1]) : 1;

    void load(category, next, false).then((ok) => {
      if (!ok) {
        window.location.href = link.href;
        return;
      }

      window.history.pushState({ category, page: next }, '', urlFor(category, next));
      goTo();
    });
  });

  // Scrolling ----------------------------------------------------------------

  if ('scroll' === mode && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          autoLoad();
        }
      },
      { rootMargin: `0px 0px ${REACH}px 0px` }
    ).observe(pagination);
  }

  // Back and forward ---------------------------------------------------------

  window.addEventListener('popstate', () => {
    const url = new URL(window.location.href);
    const cat = url.searchParams.get('category') || '';
    const match = url.pathname.match(/\/page\/(\d+)/);
    const target = match ? Number(match[1]) : 1;

    void load(cat, target, false).then((ok) => {
      if (ok) {
        markFilter(cat);
      } else {
        window.location.reload();
      }
    });
  });

  paint();
};

const init = (): void => {
  const results = document.querySelector<HTMLElement>('[data-blog-results]');

  if (!results) {
    return;
  }

  setup(results);

  // The filter row scrolls sideways on a narrow screen; arriving with the
  // active pill off the edge reads as nothing being selected.
  const row = document.querySelector<HTMLElement>('[data-blog-filters]');
  const active = row?.querySelector<HTMLElement>('.is-active');

  if (row && active && row.scrollWidth > row.clientWidth) {
    row.scrollTo({
      left: Math.max(0, active.offsetLeft - (row.clientWidth - active.offsetWidth) / 2),
      behavior: 'instant' as ScrollBehavior,
    });
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export {};
